/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import * as Layout from '@components/screen/Layout';
import {
  SociollaInput,
  SociollaFileImageList,
  SociollaSelectBox,
  SociollaMapPicker,
} from '@components/sociolla/form';

import Colors from '@global/colors';
import { getAllStates, getAllCities, getAllDistricts } from '@api/ms-account';
import { isWeb, isMobileWeb, isNative } from '@global/screens';
import { defaultCountry, isVn, checkDetailAddress } from '@global/helpers';

const QuickRegistration = (props) => {
  const [country, setCountry] = useState();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState('');
  const [districts, setDistricts] = useState([]);
  const [formEdit, setFormEdit] = useState({
    country: false,
    state: false,
    city: false,
    district: false,
  });

  const countries = useSelector((state) => state.resources.countries);
  const { setFieldValue, values } = useFormikContext();

  const onChangeState = async (selectedState) => {
    await setFieldValue('city', '', true);
    await setFieldValue('district', '', true);
    await setFieldValue('postal_code', '');
    setDistricts([]);
    getAllCities({
      params: {
        filter: { state_id: selectedState._id },
      },
    }).then((res) => {
      if (city) {
        const selectedCity = checkDetailAddress(res.data.data, city);
        if (selectedCity) {
          setFieldValue('city', selectedCity);
        }
      }
      setCity('');
      setCities(res.data.data);
      setFormEdit((prev) => ({ ...prev, city: true }));
    });
  };

  const onChangeCity = async (selectedCity) => {
    await setFieldValue('district', '', true);
    await setFieldValue('postal_code', '');
    if (isVn()) {
      return;
    }
    getAllDistricts({
      params: {
        filter: { city_id: selectedCity._id || selectedCity.id },
        sort: JSON.stringify({ name: 1 }),
      },
    }).then((res) => {
      setDistricts(res.data.data);
      setFormEdit((prev) => ({ ...prev, district: true }));
    });
  };

  const onChangeDistrict = async () => {
    await setFieldValue('postal_code', '');
  };

  const onCurrentLocation = (data) => {
    return;
  };

  useEffect(() => {
    let isMounted = true;
    if (countries?.length && !states.length) {
      const cn = countries.find(
        (x) => x.name.toLowerCase() === defaultCountry(),
      );
      setFieldValue('country', {
        name: cn.name,
        id: cn._id,
        cerebro_id: cn.odoo_country_id || '',
      });
      getAllStates({
        params: {
          filter: { country_id: cn._id },
        },
      }).then((res) => {
        if (isMounted) {
          setCountry(cn);
          setStates(res.data.data);
          setFormEdit((prev) => ({ ...prev, state: true }));
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [countries]);

  const pinCodeOptions = (values.district?.pin_codes || []).map((code) => ({
    code,
  }));
  const zipUsesDropdown = !isVn() && pinCodeOptions.length > 0;

  return (
    <View testID="quick-registration-container">
      <Layout.FormGroup style={{ marginBottom: 40 }}>
        <SociollaFileImageList
          testID="quick-registration-image-upload"
          label="Upload Photo"
          placeholderImage={require('@assets/img/icons/add-image-grey.svg')}
          name="image"
          isRequired
        />
      </Layout.FormGroup>
      <View
        style={[styles.row, isWeb ? {} : { flexDirection: 'column', flex: 1 }]}>
        <View
          style={{
            flex: !isNative ? 1 : 0,
            flexBasis: isMobileWeb ? '40%' : 0,
          }}>
          <Layout.FormGroup>
            <SociollaInput
              testID="quick-registration-name-input"
              label="Name"
              name="name"
              autoComplete="off"
              isRequired
              isUppercase
            />
          </Layout.FormGroup>

          <Layout.FormGroup>
            <SociollaSelectBox
              testID="quick-registration-country-select"
              label="Country ID"
              options={countries.data}
              value={country}
              name="country"
              trackBy="_id"
              labelKey="name"
              editable={false}
              disabled={true}
            />
          </Layout.FormGroup>

          <Layout.FormGroup>
            <SociollaSelectBox
              testID="quick-registration-state-select"
              label={isVn() ? 'City ID' : 'State ID'}
              options={states}
              name="state"
              trackBy="_id"
              labelKey="name"
              onChange={onChangeState}
              isRequired
              editable={formEdit.state}
            />
          </Layout.FormGroup>

          <Layout.FormGroup>
            <SociollaSelectBox
              testID="quick-registration-city-select"
              label={isVn() ? 'District Area' : 'City ID'}
              options={cities}
              name="city"
              trackBy="_id"
              labelKey="name"
              onChange={onChangeCity}
              editable={formEdit.city}
              disabled={!formEdit.city}
              isRequired
            />
          </Layout.FormGroup>

          {!isVn() && (
            <Layout.FormGroup>
              <SociollaSelectBox
                testID="quick-registration-district-select"
                label="District"
                options={districts}
                name="district"
                trackBy="_id"
                labelKey="name"
                onChange={onChangeDistrict}
                editable={formEdit.district}
                disabled={!formEdit.district}
                isRequired
              />
            </Layout.FormGroup>
          )}

          <Layout.FormGroup>
            <SociollaMapPicker
              testID="quick-registration-lat_long-map"
              label="Location"
              name="lat_long"
              disabled={true}
              hideInput={true}
              onCurrentLocation={(val) => onCurrentLocation(val)}
              onChangeAddress={(val) => {
                if (val.postal_code) {
                  setFieldValue('postal_code', val.postal_code);
                }
                if (val.street) {
                  setFieldValue('street', val.street);
                }
              }}
              containerStyle={{ width: '100%' }}
              isRequired
            />
          </Layout.FormGroup>

          {zipUsesDropdown ? (
            <Layout.FormGroup>
              <SociollaSelectBox
                testID="quick-registration-postal_code-select"
                label="ZIP Code"
                name="postal_code"
                options={pinCodeOptions}
                trackBy="code"
                labelKey="code"
                isRequired
                onChange={(val) =>
                  setFieldValue('postal_code', val?.code || '')
                }
              />
            </Layout.FormGroup>
          ) : (
            <Layout.FormGroup>
              <SociollaInput
                testID="quick-registration-postal_code-input"
                label="ZIP Code"
                name="postal_code"
                autoComplete="off"
                isRequired
                keyboardType="numeric"
                isNumeric={true}
              />
            </Layout.FormGroup>
          )}

          <Layout.FormGroup>
            <SociollaInput
              testID="quick-registration-street-input"
              label="Street"
              name="street"
              autoComplete="off"
              multiline={true}
              numberOfLines={4}
              isRequired
              isUppercase
            />
          </Layout.FormGroup>
        </View>

        {isWeb && <View style={styles.separator} />}

        <View style={{ flex: !isNative ? 1 : 0 }}>
          <Layout.FormGroup>
            <SociollaInput
              testID="quick-registration-contact_person-input"
              label="Person in Charge (PIC)"
              name="contact_person"
              autoComplete="off"
              isRequired
              isUppercase
            />
          </Layout.FormGroup>

          <Layout.FormGroup>
            <SociollaInput
              testID="quick-registration-mobile-input"
              label="Mobile Number"
              name="mobile"
              autoComplete="off"
              isRequired
              keyboardType="numeric"
              isNumeric={true}
            />
          </Layout.FormGroup>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    width: '100%',
  },
  separator: {
    height: '100%',
    width: 1,
    backgroundColor: Colors.grey9,
    marginHorizontal: 30,
  },
});

QuickRegistration.propTypes = {
  children: PropTypes.node,
};

QuickRegistration.defaultProps = {
  children: <></>,
};

export default QuickRegistration;
